import { Pin } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import DemoCompactHabitGrid from "./DemoCompactHabitGrid";

// Sample pinned habits with different patterns - only 2 for cleaner display
const DEMO_PINNED_HABITS = [
  {
    id: "1",
    name: "Exercise",
    color: { filled: "#22c55e", empty: "hsl(var(--habit-empty))" }, // Green with theme-aware empty
    completedDates: [
      // March 2024 - Strong consistency 
      "2024-03-01", "2024-03-02", "2024-03-04", "2024-03-05", "2024-03-07", "2024-03-08", "2024-03-09",
      // February 2024 - Good consistency with some gaps
      "2024-02-01", "2024-02-03", "2024-02-05", "2024-02-08", "2024-02-10", "2024-02-12", "2024-02-15", 
      "2024-02-16", "2024-02-17", "2024-02-19", "2024-02-20", "2024-02-23", "2024-02-24", "2024-02-25",
      "2024-02-26", "2024-02-27", "2024-02-28", "2024-02-29",
      // January 2024 - Building habit, more sporadic
      "2024-01-02", "2024-01-04", "2024-01-07", "2024-01-09", "2024-01-12", "2024-01-15", "2024-01-17",
      "2024-01-20", "2024-01-22", "2024-01-25", "2024-01-27", "2024-01-29", "2024-01-31",
      // December 2023 - Starting phase, less consistent
      "2023-12-03", "2023-12-06", "2023-12-10", "2023-12-13", "2023-12-16", "2023-12-19", 
      "2023-12-22", "2023-12-24", "2023-12-28", "2023-12-30"
    ]
  },
  {
    id: "2", 
    name: "Meditation",
    color: { filled: "#ec4899", empty: "hsl(var(--habit-empty))" }, // Pink with theme-aware empty
    completedDates: [
      // March 2024 - Weekend struggles
      "2024-03-01", "2024-03-02", "2024-03-03", "2024-03-05", "2024-03-07", "2024-03-08",
      // February 2024 - Weekday focused
      "2024-02-01", "2024-02-02", "2024-02-05", "2024-02-06", "2024-02-07", "2024-02-08", "2024-02-09",
      "2024-02-12", "2024-02-13", "2024-02-14", "2024-02-15", "2024-02-16", "2024-02-20", "2024-02-21",
      "2024-02-22", "2024-02-23", "2024-02-26", "2024-02-27", "2024-02-28", "2024-02-29",
      // January 2024 - Post-holiday recovery
      "2024-01-02", "2024-01-03", "2024-01-04", "2024-01-08", "2024-01-09", "2024-01-10", "2024-01-11",
      "2024-01-15", "2024-01-16", "2024-01-17", "2024-01-18", "2024-01-22", "2024-01-23", "2024-01-24",
      "2024-01-25", "2024-01-29", "2024-01-30", "2024-01-31",
      // December 2023 - Holiday challenges
      "2023-12-01", "2023-12-04", "2023-12-05", "2023-12-06", "2023-12-07", "2023-12-11", "2023-12-12",
      "2023-12-13", "2023-12-14", "2023-12-18", "2023-12-19", "2023-12-20"
    ]
  }
];

export default function DemoPinnedHabits() {
  const isMobile = useIsMobile();

  // Determine grid layout based on screen size - 2 columns for both
  const getGridClasses = () => {
    return "grid grid-cols-2 gap-4";
  };

  return (
    <div className="mt-6 pt-6 border-t border-border/50">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Pin className="text-primary h-5 w-5" />
            <h3 className="text-lg font-medium">Pinned Habits</h3>
          </div>
          <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full">
            2/2
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Pin your most important habits for quick access and overview
        </p>
      </div>
      <div className={getGridClasses()}>
        {DEMO_PINNED_HABITS.map((habit) => (
          <div key={habit.id} className="space-y-3 min-w-0 p-3 rounded-lg border bg-card/30 hover:bg-card/50 transition-colors overflow-hidden">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div 
                  className="w-3 h-3 rounded-sm flex-shrink-0" 
                  style={{ backgroundColor: habit.color.filled }}
                />
                <h4 className="text-sm font-medium truncate">
                  {habit.name}
                </h4>
              </div>
            </div>
            <div className="overflow-hidden w-full">
              <DemoCompactHabitGrid habit={habit} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 